import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

async function getAuthenticatedUser() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { getRequestHeader } = await import("@tanstack/react-start/server");
  const { createClient } = await import("@supabase/supabase-js");

  let token: string | undefined;
  const authHeader = getRequestHeader("authorization");
  if (authHeader) token = authHeader.replace("Bearer ", "");

  if (!token) {
    const cookieHeader = getRequestHeader("cookie") || "";
    const cookies = Object.fromEntries(
      cookieHeader.split(";").map((c) => {
        const [key, ...val] = c.trim().split("=");
        return [key, val.join("=")];
      })
    );
    const projectRef = (process.env.SUPABASE_URL || "").match(/\/\/([^.]+)/)?.[1] || "";
    const raw = cookies[`sb-${projectRef}-auth-token`];
    if (raw) {
      try {
        const parsed = JSON.parse(decodeURIComponent(raw));
        token = parsed.access_token || parsed[0];
      } catch {
        token = raw;
      }
    }
  }
  if (!token) throw new Error("No autorizado. Por favor iniciá sesión.");

  const userClient = createClient(process.env.SUPABASE_URL!, process.env.SUPABASE_PUBLISHABLE_KEY!, {
    global: { headers: { Authorization: `Bearer ${token}` } },
    auth: { persistSession: false },
  });

  const { data: userData } = await userClient.auth.getUser();
  if (!userData?.user) throw new Error("No autorizado. Por favor iniciá sesión.");

  return { userId: userData.user.id, supabaseAdmin };
}

type RepairableResaleTicket = {
  id: string;
  user_id: string;
  event_id: string;
  price: number;
  resale_price: number | null;
  events: { tenant_id: string | null } | null;
};

type ResaleListingRow = {
  id: string;
  listing_price: number;
  currency: string;
  ticket_id: string;
  event_id: string;
  seller_id: string;
  status?: string;
  created_at?: string;
};

async function hydrateResaleListings(supabaseAdmin: any, listings: ResaleListingRow[]) {
  if (listings.length === 0) return [];

  const ticketIds = Array.from(new Set(listings.map((listing) => listing.ticket_id)));
  const eventIds = Array.from(new Set(listings.map((listing) => listing.event_id)));

  const [{ data: tickets }, { data: events }] = await Promise.all([
    supabaseAdmin
      .from("tickets")
      .select("id, status, ticket_label, price, inventory_type")
      .in("id", ticketIds),
    supabaseAdmin
      .from("events")
      .select("id, title, date, time, location, city, image_url, category")
      .in("id", eventIds),
  ]);

  const ticketsById = new Map((tickets || []).map((ticket: any) => [ticket.id, ticket]));
  const eventsById = new Map((events || []).map((event: any) => [event.id, event]));

  return listings
    .map((listing) => ({
      ...listing,
      tickets: ticketsById.get(listing.ticket_id) || null,
      events: eventsById.get(listing.event_id) || null,
    }))
    .filter((listing) => (listing.tickets as any)?.status === "resale" && listing.events);
}

async function repairLegacyResaleListings(supabaseAdmin: any) {
  const { data: resaleTickets } = await supabaseAdmin
    .from("tickets")
    .select("id, user_id, event_id, price, resale_price, events(tenant_id)")
    .eq("status", "resale");

  const tickets = (resaleTickets as RepairableResaleTicket[] | null) || [];
  if (tickets.length === 0) return;

  const ticketIds = tickets.map((ticket) => ticket.id);
  const { data: existingListings } = await supabaseAdmin
    .from("resale_listings")
    .select("ticket_id")
    .in("ticket_id", ticketIds)
    .eq("status", "active");

  const existingTicketIds = new Set((existingListings || []).map((listing: { ticket_id: string }) => listing.ticket_id));
  const missingListings = tickets.filter((ticket) => !existingTicketIds.has(ticket.id));

  if (missingListings.length === 0) return;

  const { error } = await supabaseAdmin.from("resale_listings").insert(
    missingListings.map((ticket) => ({
      ticket_id: ticket.id,
      seller_id: ticket.user_id,
      event_id: ticket.event_id,
      tenant_id: ticket.events?.tenant_id || null,
      listing_price: ticket.resale_price ?? ticket.price,
      status: "active",
    }))
  );

  if (error) {
    console.error("[resale] repairLegacyResaleListings failed", error);
  }
}

/**
 * Create a resale listing for a ticket the user owns.
 * Validates ownership, status, and max resale markup (from tenant or platform settings).
 */
export const createResaleListing = createServerFn({ method: "POST" })
  .inputValidator(z.object({
    ticketId: z.string().uuid(),
    listingPrice: z.number().positive().max(100_000_000),
  }))
  .handler(async ({ data }) => {
    const { userId, supabaseAdmin } = await getAuthenticatedUser();

    await repairLegacyResaleListings(supabaseAdmin);

    const { data: ticket } = await supabaseAdmin
      .from("tickets")
      .select("id, status, user_id, price, event_id, is_free, is_resellable, events(tenant_id)")
      .eq("id", data.ticketId)
      .single();

    if (!ticket) throw new Error("Ticket no encontrado");
    if (ticket.user_id !== userId) throw new Error("Este ticket no te pertenece");
    if (ticket.status !== "active") throw new Error("Solo podés revender tickets activos");
    if ((ticket as any).is_free || (ticket as any).is_resellable === false || Number(ticket.price) <= 0) {
      throw new Error("Las entradas gratuitas no pueden ser revendidas.");
    }


    const tenantId = (ticket as any).events?.tenant_id || null;

    // Check max resale price using server-side function
    const { data: maxPriceRaw } = await supabaseAdmin.rpc("get_max_resale_price", {
      _original_price: ticket.price,
      _tenant_id: tenantId,
    });
    const maxPrice = Number(maxPriceRaw);
    if (data.listingPrice > maxPrice) {
      throw new Error(`Precio máximo permitido: ${maxPrice.toFixed(0)}`);
    }

    // Check no existing active listing
    const { data: existing } = await supabaseAdmin
      .from("resale_listings")
      .select("id")
      .eq("ticket_id", data.ticketId)
      .eq("status", "active")
      .maybeSingle();
    if (existing) throw new Error("Ya existe una publicación activa para este ticket");

    // Mark ticket as in resale
    const { error: ticketUpdateError } = await supabaseAdmin
      .from("tickets")
      .update({ status: "resale", resale_price: data.listingPrice })
      .eq("id", data.ticketId);
    if (ticketUpdateError) throw new Error(ticketUpdateError.message);

    // Create listing
    const { data: listing, error } = await supabaseAdmin
      .from("resale_listings")
      .insert({
        ticket_id: data.ticketId,
        seller_id: userId,
        event_id: ticket.event_id,
        tenant_id: tenantId,
        listing_price: data.listingPrice,
        status: "active",
      })
      .select("id")
      .single();
    if (error) {
      await supabaseAdmin
        .from("tickets")
        .update({ status: "active", resale_price: null })
        .eq("id", data.ticketId);
      throw new Error(error.message);
    }

    return { success: true, listingId: listing!.id };
  });

export const cancelResaleListing = createServerFn({ method: "POST" })
  .inputValidator(z.object({ listingId: z.string().uuid() }))
  .handler(async ({ data }) => {
    const { userId, supabaseAdmin } = await getAuthenticatedUser();

    const { data: listing } = await supabaseAdmin
      .from("resale_listings")
      .select("id, seller_id, ticket_id, status")
      .eq("id", data.listingId)
      .single();

    if (!listing) throw new Error("Publicación no encontrada");
    if (listing.seller_id !== userId) throw new Error("No te pertenece");
    if (listing.status !== "active") throw new Error("La publicación no está activa");

    await supabaseAdmin
      .from("resale_listings")
      .update({ status: "cancelled" })
      .eq("id", data.listingId);

    await supabaseAdmin
      .from("tickets")
      .update({ status: "active", resale_price: null })
      .eq("id", listing.ticket_id);

    return { success: true };
  });

export const getActiveResaleListings = createServerFn({ method: "GET" })
  .handler(async () => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    await repairLegacyResaleListings(supabaseAdmin);

    const { data } = await supabaseAdmin
      .from("resale_listings")
      .select("id, listing_price, currency, ticket_id, event_id, seller_id, created_at")
      .eq("status", "active")
      .order("created_at", { ascending: false });

    const listings = await hydrateResaleListings(supabaseAdmin, (data as ResaleListingRow[] | null) || []);
    return { listings };
  });

export const getResaleListingById = createServerFn({ method: "POST" })
  .inputValidator(z.object({ listingId: z.string().uuid() }))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: listing } = await supabaseAdmin
      .from("resale_listings")
      .select("id, listing_price, currency, ticket_id, event_id, seller_id, status, created_at")
      .eq("id", data.listingId)
      .single();

    const hydrated = listing
      ? (await hydrateResaleListings(supabaseAdmin, [listing as ResaleListingRow]))[0] || null
      : null;

    return { listing: hydrated };
  });
